import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ManageSubscription',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    workspaceId: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      isLoading: null,
      isLoadingProducts: false,
      products: [],
      freeProduct: {
        id: 'free',
        name: 'Free',
        description: 'Free tier with basic features',
        tier: 'free',
        price: {
          formatted: '0€',
          amount: 0
        },
        integrations: {
          gsc: { included: false, formatted: 'Not included' },
          cms: { included: false, formatted: 'Not included' },
          images: { included: false, formatted: 'Not included' }
        },
        autoUpdates: false,
        metadata: {
          monthly_article_credits: '3',
          user_seats: '1',
          features: JSON.stringify({
            extraDomain: { included: false, price: '+99€' },
            extraLanguage: { included: false, price: '+49€' }
          })
        }
      }
    }
  },
  async mounted () {
    await this.loadProducts()
  },
  computed: {
    ...mapGetters([
      'activeWorkspace'
    ]),
    currentTier () {
      return this.activeWorkspace && this.activeWorkspace.managed_tier
        ? this.activeWorkspace.managed_tier
        : 'free'
    },
    allProducts () {
      return [this.freeProduct, ...this.products]
    }
  },
  methods: {
    ...mapActions([
      'createStripeCheckoutSession',
      'getStripeProducts',
      'setSnackbar'
    ]),
    async loadProducts () {
      this.isLoadingProducts = true
      try {
        const response = await this.getStripeProducts()

        if (response?.products) {
          // Filter out mini tier products as they should not be shown in ManageSubscription
          // Only show ContentGecko Main products (those with priceId)
          this.products = response.products.filter(product => {
            const tier = product.tier || product.metadata?.tier
            return tier !== 'mini' && product.priceId // Only show main products with priceId
          })
        }
      } catch (error) {
        console.error('Error loading products:', error)
        this.setSnackbar({
          open: true,
          text: 'Error loading subscription products',
          type: 'error'
        })
      }
      this.isLoadingProducts = false
    },
    getMonthlyArticles (product) {
      const credits = product.metadata?.monthly_article_credits || 'Contact us'
      return credits === '999' ? 'Unlimited' : credits
    },
    getUserSeats (product) {
      const seats = product.metadata?.user_seats || '1 seat'
      return seats === '99' ? 'Unlimited' : seats
    },
    getFeatures (product) {
      try {
        return product.metadata?.features ? JSON.parse(product.metadata.features) : {}
      } catch (error) {
        console.error('Error parsing features from metadata:', error)
        return {}
      }
    },
    getIntegrations (product) {
      return product.integrations || {}
    },
    getIntegrationName (key) {
      const integrationNames = {
        gsc: 'Google Search Console',
        cms: 'CMS Integration',
        images: 'Images'
      }
      return integrationNames[key] || key
    },
    close () {
      this.$emit('close')
    },
    async handleSubscription (product) {
      if (this.isCurrentTier(product)) return // Already on this tier

      // Free tier doesn't require Stripe checkout
      if (product.id === 'free') {
        this.setSnackbar({
          open: true,
          text: 'To downgrade to free, please cancel your subscription through the billing portal.',
          type: 'info'
        })
        return
      }

      this.isLoading = product.priceId || product.id
      try {
        // For ContentGecko Main products, use priceId
        const checkoutData = {
          workspaceId: this.workspaceId
        }

        if (product.priceId) {
          checkoutData.priceId = product.priceId
        } else {
          checkoutData.productId = product.id
        }

        const response = await this.createStripeCheckoutSession(checkoutData)

        if (!response || !response.url) throw new Error('No response from createStripeCheckoutSession')

        // Check if this was a subscription update or new checkout (open in new tab)
        if (response.subscription_updated) {
          this.setSnackbar({
            open: true,
            text: 'Subscription updated successfully! Refresh the page to continue',
            type: 'success'
          })
        } else {
          // Open in new tab for new subscriptions
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: 'Error processing subscription: ' + (error.message || 'Unknown error'),
          type: 'error'
        })
      }
      this.isLoading = null
    },
    isCurrentTier (product) {
      // For free tier
      if (product.id === 'free') {
        return this.currentTier === 'free'
      }

      // For paid tiers, check if the product tier matches current tier
      const productTier = product.tier || product.metadata?.tier
      return this.currentTier === productTier
    },
    getFeatureName (key) {
      const featureNames = {
        cmsIntegration: 'CMS Integration',
        gscIntegration: 'GSC Integration',
        images: 'Images',
        extraDomain: 'Extra Domain',
        extraLanguage: 'Extra Language'
      }
      return featureNames[key] || key
    },
    isProductLoading (product) {
      return this.isLoading === (product.priceId || product.id)
    }
  }
}
